import { ReactElement, useEffect } from 'react';
import { Address } from 'viem';

import { useGetUnstakePeriodQuery } from 'modules/api/actions/getUnstakePeriod';
import { useConnection } from 'modules/auth/hooks/useConnection';
import successImage from 'modules/common/assets/unstake-success.png';
import { SuccessDialogContent } from 'modules/common/components/SuccessDialogContent/SuccessDialogContent';
import { KnownDialogs, useDialog } from 'modules/dialogs';
import { useTranslation } from 'modules/i18n';
import { useGetPoolQuery } from 'modules/pool/actions/getPool';
import { useWithdrawMutation } from 'modules/withdraw/actions/withdraw';
import { WithdrawForm } from 'modules/withdraw/components/WithdrawForm';

import { translation } from './translation';

interface IWithdrawContentProps {
  poolAddress: Address;
}

export function WithdrawContent({
  poolAddress,
}: IWithdrawContentProps): ReactElement | null {
  const { t, keys } = useTranslation(translation);
  const { onClose } = useDialog(KnownDialogs.withdraw);
  const { isSafeAccount, cacheTime } = useConnection();

  const { data: unstakePeriodDays } = useGetUnstakePeriodQuery(
    { poolAddress },
    { refetchOnMountOrArgChange: cacheTime },
  );
  const { data: pool } = useGetPoolQuery(
    { address: poolAddress },
    { refetchOnMountOrArgChange: cacheTime },
  );
  const [withdraw, { isLoading, data }] = useWithdrawMutation();

  useEffect(() => {
    if (!isLoading && data && isSafeAccount) {
      onClose();
    }
  }, [isLoading, data, isSafeAccount, onClose]);

  if (!pool) {
    return null;
  }

  if (!isLoading && data && !isSafeAccount) {
    return (
      <SuccessDialogContent
        buttonLabel={t(keys.buttonText)}
        description={
          unstakePeriodDays
            ? t(keys.doneDescription, { period: unstakePeriodDays.toNumber() })
            : undefined
        }
        image={successImage}
        title={t(keys.done)}
        onClose={onClose}
      />
    );
  }

  return (
    <WithdrawForm
      isSubmitLoading={isLoading}
      poolAddress={poolAddress}
      onSubmit={({ amount }) => withdraw({ amount, poolAddress })}
    />
  );
}
